import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import validator from 'validator';
import { getData } from 'country-list';
import { loadStripe } from '@stripe/stripe-js';
import CheckoutForm from './CheckoutForm';
import MultibancoDetails from './MultibancoDetails';
import { decodePaymentToken } from '../utils/paymentToken';
import './LandingPage.css';

export default function LandingPage() {
  const { token } = useParams();

  // Pode ser: '', 'invalid_local', 'expired', 'waiting_server', 'server_invalid', 'valid'
  const [tokenStatus, setTokenStatus] = useState('');

  // Dados decodificados do token
  const [tokenData, setTokenData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    value: '',
    package: '',
    country: '',
  });

  const [errors, setErrors] = useState({});

  // Novo estado para controlar se o submit está em andamento
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fluxo novo (Stripe direto): quando o backend devolve client_secret,
  // mostramos o Stripe Elements aqui em vez de redirecionar
  const [checkout, setCheckout] = useState(null); // { clientSecret, publishableKey, provider, intentId }

  // Token já traz os dados do cliente -> saltamos o formulário e vamos direto ao
  // checkout. Decidido no decode do token (estável), para o formulário nunca chegar
  // a renderizar quando não é preciso — nem um frame.
  const [autoFlow, setAutoFlow] = useState(false);
  const [autoFailed, setAutoFailed] = useState(false);
  const autoCheckoutTried = useRef(false);

  // Pagamento já tem referência Multibanco atribuída -> mostramos os dados no nosso UI
  const [mbDetails, setMbDetails] = useState(null);

  const stripePromise = useMemo(
    () => (checkout && checkout.provider !== 'airwallex' ? loadStripe(checkout.publishableKey) : null),
    [checkout]
  );

  // ---------- PAÍSES: 4 no topo (PT, ES, GB, FR) + todos ----------
  const topCountries = [
    { name: 'Portugal', code: 'PT' },
    { name: 'Espanha', code: 'ES' },
    { name: 'Inglaterra', code: 'GB' },
    { name: 'França', code: 'FR' },
  ];
  const allCountries = getData().filter(
    (c) => !topCountries.find((tc) => tc.code === c.code)
  );
  allCountries.sort((a, b) => a.name.localeCompare(b.name, 'pt'));
  const combinedCountries = [...topCountries, ...allCountries];

  // ----------- 1) Decodificar token localmente -----------
  useEffect(() => {
    if (!token) {
      setTokenStatus('invalid_local');
      return;
    }

    try {
      const parsed = decodePaymentToken(token);

      const {
        username,
        timestamp,
        value,
        package: pkg,
        firstName,
        lastName,
        email,
        country,
      } = parsed;

      // Checar campos mínimos
      if (!username || !timestamp || !value || !pkg) {
        setTokenStatus('invalid_local');
        return;
      }

      // Se passou pela validação local, vamos perguntar ao servidor
      setTokenData({
        username: username || '',
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        value: value || '',
        package: pkg || '',
        country: country || '',
      });

      // Dados completos no token -> fluxo automático, o formulário nunca aparece
      setAutoFlow(
        !!(firstName && lastName && country && validator.isEmail(email || ''))
      );

      setTokenStatus('waiting_server');
    } catch (err) {
      console.error('Invalid Token');
      setTokenStatus('invalid_local');
    }
  }, [token]);

  // ----------- 2) Se token local for válido, checamos no servidor -----------
  useEffect(() => {
    const verifyOnServer = async () => {
      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API_URL}/v2/verify-token`,
          { token }
        );

        if (data.isValid) {
          setTokenStatus('valid');
        } else if (data.multibanco) {
          // Referência Multibanco já gerada => mostrar os dados no nosso UI
          setMbDetails(data.multibanco);
        } else if (data.payment_link) {
          // Fluxo antigo (Paytree) => redirecionar.
          // replace: o "voltar" do browser não regressa a esta página.
          window.location.replace(data.payment_link);
        } else {
          setTokenStatus('server_invalid');
        }
      } catch (error) {
        console.error('Erro ao verificar token no servidor:', error);
        setTokenStatus('server_invalid');
      }
    };

    if (tokenStatus === 'waiting_server') {
      verifyOnServer();
    }
  }, [tokenStatus, token]);

  // ----------- 3) Lidar com campos do form e submit -----------
  const handleChange = (field, value) => {
    setTokenData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const getClientIp = async () => {
    let clientIp = '';
    try {
      const ipResponse4 = await axios.get('https://api4.ipify.org?format=json');
      clientIp = ipResponse4.data.ip;
    } catch (errIPv4) {
      console.warn('Erro IPv4, tentando IPv6...');
      try {
        const ipResponse6 = await axios.get(
          'https://api6.ipify.org?format=json'
        );
        clientIp = ipResponse6.data.ip;
      } catch (errIPv6) {
        console.warn('Não foi possível obter IPv4 nem IPv6');
      }
    }
    return clientIp;
  };

  // Pede o intent ao backend e decide o destino:
  // client_secret -> checkout Stripe nesta página; payment_link -> redirect (Paytree/voucher)
  const requestIntent = async (data) => {
    const clientIp = await getClientIp();

    const payload = {
      username: data.username,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      email: data.email.trim(),
      value: data.value,
      package: data.package,
      country: data.country,
      userAgent: navigator.userAgent,
      clientIp,
      token
    };

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/v2/create-intent`,
      payload
    );

    if (response.status === 200 && response.data?.client_secret) {
      // Corp com domínio próprio de checkout -> levar o cliente para lá.
      // O client_secret vai no fragment (#): não aparece em logs de servidor.
      if (response.data.checkout_url) {
        const params = new URLSearchParams({
          cs: response.data.client_secret,
          pk: response.data.publishable_key,
          // pv/pi: qual o processador e o id do intent. Links antigos não os
          // levam e a página de checkout assume Stripe, como antes.
          pv: response.data.provider || 'stripe',
          pi: response.data.intent_id || '',
          // Para onde voltar depois de pagar. Vem do gateway com uma referência
          // opaca — o processador não vê quem é o cliente.
          su: response.data.success_url || '',
        });
        window.location.replace(
          `${response.data.checkout_url}/checkout/${token}#${params.toString()}`
        );
        return true;
      }

      setCheckout({
        clientSecret: response.data.client_secret,
        publishableKey: response.data.publishable_key,
        provider: response.data.provider || 'stripe',
        intentId: response.data.intent_id || '',
        successUrl: response.data.success_url || '',
      });
      return true;
    }

    if (response.status === 200 && response.data?.payment_link) {
      window.location.replace(response.data.payment_link);
      return true;
    }

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Bloqueia o botão para evitar multi-click
    setIsSubmitting(true);

    // Validação extra do email
    if (!validator.isEmail(tokenData.email)) {
      setErrors((prev) => ({
        ...prev,
        email: 'Digite um email válido',
      }));
      setIsSubmitting(false); // reabilita caso dê erro
      return;
    }

    if (!tokenData.country) {
      setErrors((prev) => ({
        ...prev,
        country: 'Selecione um país',
      }));
      setIsSubmitting(false); // reabilita caso dê erro
      return;
    }

    try {
      const ok = await requestIntent(tokenData);
      if (!ok) {
        alert('Não foi possível concluir o pagamento, tente mais tarde.');
      }
    } catch (error) {
      console.error('Erro ao enviar');
      alert('Não foi possível concluir o pagamento, tente mais tarde.');
    } finally {
      // Reabilita o botão após a requisição (sucesso ou erro)
      setIsSubmitting(false);
    }
  };

  // Fluxo automático: pede o intent sem nunca mostrar o formulário.
  // Só se falhar é que o formulário aparece (fallback).
  useEffect(() => {
    const autoCheckout = async () => {
      try {
        const ok = await requestIntent(tokenData);
        if (!ok) {
          console.warn('Auto-checkout falhou, a mostrar formulário');
          setAutoFailed(true);
        }
      } catch (error) {
        console.error('Erro no auto-checkout, a mostrar formulário');
        setAutoFailed(true);
      }
    };

    if (tokenStatus === 'valid' && autoFlow && !autoCheckoutTried.current) {
      autoCheckoutTried.current = true;
      autoCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenStatus, autoFlow]);

  // ----------- 4) Render conforme tokenStatus -----------

  // Referência Multibanco já atribuída: mostrar os dados no nosso UI
  if (mbDetails) {
    return <MultibancoDetails details={mbDetails} />;
  }

  if (tokenStatus === 'invalid_local') {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">Não há nada para ver aqui!!</h2>
          <p className="messageText">Contacte o administrador</p>
        </div>
      </div>
    );
  }

  if (tokenStatus === 'waiting_server') {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">Verificando...</h2>
          <p className="messageText">
            Aguarde enquanto confirmamos a validade.
          </p>
        </div>
      </div>
    );
  }

  if (tokenStatus === 'server_invalid') {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">Não há nada para ver aqui!!</h2>
          <p className="messageText">Contacte o administrador</p>
        </div>
      </div>
    );
  }

  if (tokenStatus !== 'valid') {
    // Exemplo: se for '', null, etc. (poderia ser um loading)
    return null;
  }

  // Passo de pagamento: temos client_secret -> checkout Stripe (página inteira)
  if (checkout && (stripePromise || checkout.provider === 'airwallex')) {
    return (
      <CheckoutForm
        provider={checkout.provider}
        stripePromise={stripePromise}
        publishableKey={checkout.publishableKey}
        intentId={checkout.intentId}
        returnUrl={checkout.successUrl}
        clientSecret={checkout.clientSecret}
        token={token}
        email={tokenData.email.trim()}
        fullName={`${tokenData.firstName} ${tokenData.lastName}`.trim()}
        value={tokenData.value}
        username={tokenData.username}
        packageName={tokenData.package}
      />
    );
  }

  // Fluxo automático em curso (token com dados completos): mostrar loading até o
  // checkout montar ou o redirect acontecer — o formulário nunca chega a aparecer.
  // Só cai para o formulário se o auto-checkout falhar.
  if (autoFlow && !autoFailed) {
    return (
      <div className="pageContainer">
        <div className="messageContainer">
          <h2 className="messageTitle">A preparar o pagamento...</h2>
          <p className="messageText">Aguarde um momento.</p>
        </div>
      </div>
    );
  }

  // Se tokenStatus === 'valid'
  return (
    <div className="pageContainer">
      <div className="formContainer">
        <h2 className="formTitle">Username: {tokenData.username}</h2>
        <h4 className="subTitle">
          {tokenData.package} - {tokenData.value} Euros
        </h4>

        <form onSubmit={handleSubmit} className="form">
          <label className="label">Primeiro Nome:</label>
          <input
            type="text"
            className="input"
            value={tokenData.firstName}
            onChange={(e) => handleChange('firstName', e.target.value)}
            required
          />
          {errors.firstName && <div className="error">{errors.firstName}</div>}

          <label className="label">Último Nome:</label>
          <input
            type="text"
            className="input"
            value={tokenData.lastName}
            onChange={(e) => handleChange('lastName', e.target.value)}
            required
          />
          {errors.lastName && <div className="error">{errors.lastName}</div>}

          <label className="label">Email:</label>
          <input
            type="email"
            className="input"
            value={tokenData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
          />
          {errors.email && <div className="error">{errors.email}</div>}

          <label className="label">País:</label>
          <select
            className="select"
            value={tokenData.country}
            onChange={(e) => handleChange('country', e.target.value)}
            required
          >
            <option value="">Selecione um país</option>
            {combinedCountries.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
          {errors.country && <div className="error">{errors.country}</div>}

          {/* Botão desabilitado enquanto isSubmitting === true */}
          <button
            type="submit"
            className="submitButton"
            disabled={isSubmitting}
          >
            {/* Texto do botão muda se estiver enviando */}
            {isSubmitting ? 'A pedir pagamento...' : 'Confirmar renovação'}
          </button>
        </form>
      </div>
    </div>
  );
}
